/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as React from "react";
// import { Alert, IndeterminateProgressBar, Panel } from '@partnercenter-react/ui-webcore';
import { LocalizationContext } from "../../context/LocalizationContext";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { EnrollmentDetail } from "../../containers/base-container";
import { DefaultPaymentProfileAssignmentDetails } from "../../models/model";
import { RoutesHelper } from "../../utilities/routesHelper";
import button  from "@harmony/enablers/components/button/button";
import { createScope }  from "@harmony/enablers/react/scope-for-react";
import partnerCenterTheme  from "@harmony/enablers/themes/partner-center";
import flyInPanel  from "@harmony/enablers/components/fly-in-panel/fly-in-panel";
import messageBar  from "@harmony/enablers/components/message-bar/message-bar";
import progress  from "@harmony/enablers/components/progress/progress";

export interface EnrollmentBankAndTaxWithDefaultPanelProps {
  visible: boolean;
  isLoading: boolean;
  invitationDetail: EnrollmentDetail;
  defaultPaymentDetails: DefaultPaymentProfileAssignmentDetails;
  showSubmitErrorBanner: boolean;
  isSubmitLoading: boolean;
}

export interface EnrollmentBankAndTaxWithDefaultPanelbackProps {
  onClose: () => void;
  onSubmit: () => void;
  onRedirect: () => void;
  onDefaultNotChosen: () => void;
}

const scope = createScope({
  reactInstance: React,
  styles: [partnerCenterTheme],
});

const FlyInPanel = scope.forReact(flyInPanel);
const Progress = scope.forReact(progress);
const MessageBar = scope.forReact(messageBar);
const Button = scope.forReact(button);

type EnrollmentBankAndTaxWithDefaultPanelPropsType =
  EnrollmentBankAndTaxWithDefaultPanelProps &
    EnrollmentBankAndTaxWithDefaultPanelbackProps;

export class EnrollmentBankAndTaxWithDefaultPanel extends React.Component<EnrollmentBankAndTaxWithDefaultPanelPropsType> {
  private programNameNoSpace: string = "";
  private firstFocusElement = React.createRef<HTMLAnchorElement>();

  constructor(props: EnrollmentBankAndTaxWithDefaultPanelPropsType) {
    super(props);
  }

  render(): JSX.Element {
    return (
      <LocalizationContext.Consumer>
        {(localization) => this.renderContent(localization)}
      </LocalizationContext.Consumer>
    );
  }

  private renderContent(localization: LocalizationInfo) {
    if (!this.props.invitationDetail) {
      return null;
    }

    this.programNameNoSpace = this.props.invitationDetail.programName.replace(
      /\s+/g,
      ""
    );

    return (
      <FlyInPanel
        style={{ "--size": "var(--he-panel-size-small)" }}
        open={this.props.visible}
        onHeAfterHide={this.props.onClose}
      >
        <div slot="heading" style={{ marginTop: 100 }}>
          <strong>
            {localization.format(
              "EnrollInHeading",
              this.props.invitationDetail.programName
            )}
          </strong>
        </div>
        <div>
          {this.props.isSubmitLoading && (
            <>
              <div className="text-subtitle spacer-xs-top"></div>
              <Progress label="Progress" indeterminate />
            </>
          )}

          {this.props.showSubmitErrorBanner && (
            <MessageBar appearance="warning">
              {localization.strings["UnsuccessfulSubmitWithDefaults"]}
            </MessageBar>
          )}

          <p>
            <>
              <p className="spacer-xxs-top">
                {this.props.invitationDetail.locationName} (
                {this.props.invitationDetail
                  ? `${this.props.invitationDetail.partnerId.type}: ${this.props.invitationDetail.partnerId.value}`
                  : ""}
                )<br />
                {this.props.invitationDetail.invitationOfferingTimeline
                  ? this.props.invitationDetail.invitationOfferingTimeline.name
                  : ""}
              </p>

              <div className="spacer-ml-top">
                <p>
                  {this.transformTextWithBoldDetails(
                    localization.strings["CompletePaymentAndTax"]
                  )}{" "}
                </p>
                <br />
                {!this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  !this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  this.props.defaultPaymentDetails
                    .taxProfileCompleteForProgram &&
                  this.props.defaultPaymentDetails.currencyUsable && (
                    <p>
                      {this.transformToClickableLink(
                        localization.strings["DefaultBankUseable"],
                        RoutesHelper.getBankAndTaxRoute(
                          this.props.invitationDetail.partnerId.value +
                            "_" +
                            this.props.invitationDetail.offeringGuid,
                          false
                        ),
                        this.firstFocusElement
                      )}
                    </p>
                  )}
                {this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  !!this.props.defaultPaymentDetails.taxProfilePartnerId && (
                    <p>{localization.strings["DefaultBankActionRequired"]}</p>
                  )}
                {this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  (this.props.defaultPaymentDetails.taxProfilePartnerId ==
                    null ||
                    this.props.defaultPaymentDetails.taxProfilePartnerId ==
                      undefined) && (
                    <p>{localization.strings["DefaultBankAndNoTax"]}</p>
                  )}
                {this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  !this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  !this.props.defaultPaymentDetails.currencyUsable &&
                  this.props.defaultPaymentDetails
                    .taxProfileCompleteForProgram && (
                    <p>
                      {localization.strings["DefaultBankCurrencyNotUseable"]}
                    </p>
                  )}
                {this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  !this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  this.props.defaultPaymentDetails.currencyUsable &&
                  !this.props.defaultPaymentDetails
                    .taxProfileCompleteForProgram && (
                    <p>{localization.strings["DefaultBankTaxNotUsable"]}</p>
                  )}
                {this.props.defaultPaymentDetails
                  .userActionRequiredForCurrentDefaultSelection &&
                  !this.props.defaultPaymentDetails
                    .paymentProfileActionRequired &&
                  !this.props.defaultPaymentDetails.currencyUsable &&
                  !this.props.defaultPaymentDetails
                    .taxProfileCompleteForProgram && (
                    <p>
                      {
                        localization.strings[
                          "DefaultBankTaxAndCurrencyNotUsable"
                        ]
                      }
                    </p>
                  )}
              </div>

              <div className="spacer-xs-top">
                <div className="row spacer-ml-top">
                  <div className="col-xs-8">
                    <div className="spacer-xxs-top">
                      <strong>{localization.strings["Bank"]}</strong>
                      <br />

                      <div className="spacer-xxs-top">
                        <label className="form-label">
                          {localization.strings["Name"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails.bankProfileItemDetail
                          ? this.props.defaultPaymentDetails
                              .bankProfileItemDetail.profileName
                          : localization.strings["DashText"]}
                        <br />
                        <label className="form-label">
                          {localization.strings["Country"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails.bankProfileItemDetail
                          ? this.props.defaultPaymentDetails
                              .bankProfileItemDetail.bankCountryName
                          : localization.strings["DashText"]}
                        <br />
                        <label className="form-label">
                          {localization.strings["LastFourDigits"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails.bankProfileItemDetail
                          ? this.props.defaultPaymentDetails
                              .bankProfileItemDetail.accountLastFourDigits
                          : localization.strings["DashText"]}
                        <br />
                        <label className="form-label">
                          {localization.strings["Currency"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails.selection
                          ? this.props.defaultPaymentDetails.selection.currency
                          : localization.strings["DashText"]}
                      </div>
                    </div>

                    <br />
                    <div className="spacer-xxs-top">
                      <strong>{localization.strings["TaxProfile"]}</strong>
                      <div className="spacer-xxs-top">
                        <label className="form-label">
                          {localization.strings["PartnerId"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails.taxProfilePartnerId
                          ? this.props.defaultPaymentDetails.taxProfilePartnerId
                              .value
                          : localization.strings["DashText"]}
                        <br />
                        <label className="form-label">
                          {localization.strings["Country"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails
                          .taxProfilePartnerIdInfo
                          ? this.props.defaultPaymentDetails
                              .taxProfilePartnerIdInfo.countryName
                          : localization.strings["DashText"]}
                        <br />
                        <label className="form-label">
                          {localization.strings["CompanyName"]}
                        </label>{" "}
                        {this.props.defaultPaymentDetails
                          .taxProfilePartnerIdInfo
                          ? this.props.defaultPaymentDetails
                              .taxProfilePartnerIdInfo.companyName
                          : localization.strings["DashText"]}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </p>
        </div>
        <div slot="footer">
          {!this.props.defaultPaymentDetails
            .userActionRequiredForCurrentDefaultSelection &&
            this.props.defaultPaymentDetails.taxProfileCompleteForProgram &&
            this.props.defaultPaymentDetails.currencyUsable && (
              <div
                className="spacer-xs-top"
                style={{ display: "flex", gap: 10 }}
              >
                <Button
                  type="submit"
                  appearance="primary"
                  onClick={() => this.props.onSubmit()}
                  data-bi-slot="1"
                  data-bi-id={
                    "BankAndTaxWithDefaultSubmit-" + this.programNameNoSpace
                  }
                >
                  {localization.strings["Submit"]}
                </Button>
                <Button
                  type="reset"
                  appearance="secondary"
                  onClick={() => this.props.onClose()}
                  data-bi-slot="2"
                  data-bi-id={
                    "BankAndTaxModalWithDefaultClose-" + this.programNameNoSpace
                  }
                >
                  {localization.strings["CancelModalText"]}
                </Button>
              </div>
            )}

          {(this.props.defaultPaymentDetails
            .userActionRequiredForCurrentDefaultSelection ||
            !this.props.defaultPaymentDetails.currencyUsable ||
            !this.props.defaultPaymentDetails.taxProfileCompleteForProgram) && (
            <div className="spacer-xs-top" style={{ display: "flex", gap: 10 }}>
              <Button
                type="submit"
                appearance="primary"
                external
                onClick={() => this.props.onRedirect()}
                href={RoutesHelper.getBankAndTaxRoute(
                  this.props.invitationDetail.partnerId.value +
                    "_" +
                    this.props.invitationDetail.offeringGuid,
                  true
                )}
                target="_blank"
                data-bi-slot="1"
                data-bi-id={
                  "BankAndTaxWithDefaultRedirect-" + this.programNameNoSpace
                }
              >
                {localization.strings["Continue"]}
              </Button>
              <Button
                type="reset"
                appearance={"secondary"}
                onClick={() => this.props.onClose()}
                data-bi-slot="2"
                data-bi-id={
                  "BankAndTaxModalWithDefaultClose-" + this.programNameNoSpace
                }
              >
                {localization.strings["CancelModalText"]}
              </Button>
            </div>
          )}
        </div>
      </FlyInPanel>
    );
  }

  private transformTextWithBoldDetails(text: string): JSX.Element {
    if (!text) {
      return <></>;
    }
    const splitText = text.split("<b>");
    if (splitText.length < 2) {
      return <>{text}</>;
    }

    const boldText = splitText[1].split("</b>");
    if (boldText.length < 2) {
      return <>{text}</>;
    }

    return (
      <>
        {splitText[0]}
        <strong>{boldText[0]}</strong>
        {boldText[1]}
      </>
    );
  }

  private transformToClickableLink(
    text: string,
    url: string,
    reference: React.RefObject<HTMLAnchorElement>
  ): JSX.Element {
    if (!text) {
      return <></>;
    }
    const splitText = text.split("<a>");
    if (splitText.length < 2) {
      return <>{text}</>;
    }

    const linkText = splitText[1].split("</a>");
    if (linkText.length < 2) {
      return <>{text}</>;
    }

    return (
      <>
        {splitText[0]}
        <a
          href={url}
          target="_blank"
          ref={reference}
          onClick={() => this.props.onDefaultNotChosen()}
        >
          {linkText[0]}
        </a>
        {linkText[1]}
      </>
    );
  }
}
